export function toJson(str: string): string {
  try {
    const parsed = JSON.parse(str);
    return JSON.stringify(parsed, null, 2);
  } catch {
    // Try converting Python repr format to JSON
    try {
      const normalized = pythonReprToJson(str);
      const parsed = JSON.parse(normalized);
      return JSON.stringify(parsed, null, 2);
    } catch {
      // Fall back to text-based formatting for invalid JSON-like syntax
      return formatJsonLike(str);
    }
  }
}

/**
 * Formats JSON-like syntax without requiring valid JSON.
 * Handles single-quoted keys, unquoted keys/values, and adds proper indentation.
 */
export function formatJsonLike(str: string): string {
  const tokens = tokenizeJsonLike(str);
  if (!tokens.length) return str;

  let result = "";
  let indent = 0;
  const INDENT = "  ";

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const prevToken = tokens[i - 1];
    const nextToken = tokens[i + 1];

    if (token === "{" || token === "[") {
      // Check if empty object/array
      if (nextToken === "}" || nextToken === "]") {
        result += token;
      } else {
        result += token + "\n" + INDENT.repeat(++indent);
      }
    } else if (token === "}" || token === "]") {
      // Don't decrease indent for empty containers (already handled above)
      if (prevToken !== "{" && prevToken !== "[") {
        result += "\n" + INDENT.repeat(--indent) + token;
      } else {
        result += token;
      }
    } else if (token === ",") {
      result += ",\n" + INDENT.repeat(indent);
    } else if (token === ":") {
      result += ": ";
    } else {
      result += token;
    }
  }

  return result;
}

/**
 * Tokenizes JSON-like string into structural tokens and value tokens.
 * Handles: quoted strings (single/double), unquoted identifiers, numbers, booleans, null.
 */
function tokenizeJsonLike(str: string): string[] {
  const tokens: string[] = [];
  let i = 0;

  while (i < str.length) {
    const char = str[i];

    // Skip whitespace
    if (/\s/.test(char)) {
      i++;
      continue;
    }

    // Structural characters
    if ("{[}]:,".includes(char)) {
      tokens.push(char);
      i++;
      continue;
    }

    // Quoted string (single or double)
    if (char === '"' || char === "'") {
      const quote = char;
      let value = quote;
      i++;
      while (i < str.length) {
        if (str[i] === "\\") {
          value += str[i] + (str[i + 1] ?? "");
          i += 2;
        } else if (str[i] === quote) {
          value += quote;
          i++;
          break;
        } else {
          value += str[i];
          i++;
        }
      }
      tokens.push(value);
      continue;
    }

    // Unquoted value (identifier, number, boolean, null, etc.)
    let value = "";
    while (i < str.length && !/[\s{}[\]:,]/.test(str[i])) {
      value += str[i];
      i++;
    }
    if (value) {
      tokens.push(value);
    }
  }

  return tokens;
}

/**
 * Converts Python repr string format to valid JSON.
 * Handles: single quotes, True/False/None, datetime.datetime objects
 */
export function pythonReprToJson(str: string): string {
  let result = str;

  // 1. Handle datetime.datetime(year, month, day, hour?, min?, sec?, micro?) → ISO string
  result = result.replace(
    /datetime\.datetime\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+))?(?:,\s*(\d+))?(?:,\s*(\d+))?(?:,\s*(\d+))?\)/g,
    (_, year, month, day, hour = "0", minute = "0", second = "0", micro = "0") => {
      const isoDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:${second.padStart(2, "0")}`;
      const ms = Math.floor(+micro / 1000);
      return `"${isoDate}${ms ? `.${ms.toString().padStart(3, "0")}` : ""}"`;
    }
  );

  // 2. Replace Python booleans/None when they appear as values (after : , or [)
  result = result.replace(
    /([:,[]\s*)(True|False|None)(?=\s*[,}\]])/g,
    (_, before, value) => {
      const jsonValue =
        value === "True" ? "true" : value === "False" ? "false" : "null";
      return before + jsonValue;
    }
  );

  // 3. Replace single quotes with double quotes
  result = result.replace(/'/g, '"');

  return result;
}
